import { useEffect, useLayoutEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiButton, MiContentDrawer, MiIcon } from "@miplatform/components";
import { IPriceLevelResponse } from "@miplatform/types";
import { mdiClose, mdiHistory } from "@mdi/js";
import { ActivityDateRange, MiActivityLog } from "@mibuildweb/components";
import { usePriceLevels } from "@mibuildweb/hooks";
import { Box, Typography } from "@mui/material";

export function ActivityLogDrawer({ open, onClose, priceLevels }: ActivityLogDrawerProps) {
  const { activities, isLoading, getPriceLevelActivities } = usePriceLevels();
  const { classes, theme } = useStyles();
  const [dateRange, setDateRange] = useState<ActivityDateRange>({
    startDate: null,
    endDate: null,
  });
  const [selectedPriceLevelId, setSelectedPriceLevelId] = useState<string | undefined>(undefined);

  useLayoutEffect(() => {
    if (!open) {
      setDateRange({ startDate: null, endDate: null });
      setSelectedPriceLevelId(undefined);
    }
  }, [open]);

  const selectedPriceLevelReferenceId = priceLevels.find(
    (priceLevel) => priceLevel.id === selectedPriceLevelId
  )?.referenceId;

  useEffect(() => {
    if (open) {
      getPriceLevelActivities(dateRange, selectedPriceLevelReferenceId);
    }
  }, [open, dateRange, selectedPriceLevelReferenceId]);

  const priceLevelOptions = priceLevels.map((priceLevel) => ({
    label: priceLevel.name,
    value: priceLevel.id,
  }));

  return (
    <MiContentDrawer
      open={open}
      width={500}
      Header={
        <Box className={classes.headerBox}>
          <Box className={classes.headerTitleBox}>
            <MiIcon path={mdiHistory} color={theme.palette.primary.main} />
            <Typography fontWeight={theme.fontWeight.semiBold}>
              Activity Log
            </Typography>
          </Box>
          <MiButton
            startIcon={{ path: mdiClose, iconColor: theme.palette.grey.main }}
            onClick={onClose}
          />
        </Box>
      }
    >
      <MiActivityLog
        activities={activities}
        isLoading={isLoading}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        showDateRangePicker
        showActivityCardBorder
        showPriceLevelFilter
        priceLevelOptions={priceLevelOptions}
        selectedPriceLevelId={selectedPriceLevelId}
        onPriceLevelChange={setSelectedPriceLevelId}
      />
    </MiContentDrawer>
  );
}

const useStyles = makeStyles()((theme) => ({
  headerBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey.lightAccent,
  },
  headerTitleBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}));

interface ActivityLogDrawerProps {
  open: boolean;
  onClose: () => void;
  priceLevels: IPriceLevelResponse[];
}
